import React from 'react';
import { FormattedMessage } from 'react-intl';
import reactStringReplace from 'react-string-replace';
import { Typography } from '@popmenu/common-ui';

import { useQuery } from '~/lazy_apollo/client';
import { PopmenuTermsDocument } from '~/libs/gql/queries/restaurants/popmenuTermsQuery.generated';
import { useRestaurantPublishedLegalDocumentsQuery } from '~/libs/gql/queries/restaurants/restaurantPublishedLegalDocumentsQuery.generated';
import type { PopmenuTermsQuery_popmenuTerms_PopmenuTerms as PopmenuTermsType } from '~/libs/gql/queries/restaurants/popmenuTermsQuery.generated';

import { useRestaurant } from '../../utils/withRestaurant';
import { toCamelCase } from '../../utils/strings';
import { AH } from './AccessibleHeading';
import Loading from '../../shared/Loading';
import TermsHtmlContent from './TermsHtmlContent';
import { useTermsMessages } from './useTermsMessages';

export type TermsType = 'clientAccessibility' | 'clientTerms' | 'dmcaPolicy' | 'giveawayTerms' | 'popmenuPrivacy' | 'privacy' | 'terms'

type TermsContainerProps = {
  termsType: TermsType
  id?: string
  hideTitle?: boolean
  /** For apps that do not provide the consumer restaurant context -- the ordering app
   *  keeps its own, so its dialogs pass the id in. */
  restaurantId?: number
}

const RESTAURANT_DOCUMENT_TYPES = {
  privacy: 'privacy_policy',
  terms: 'terms_of_use',
} as const;

const TermsContainer = ({ hideTitle = false, termsType, id = 'terms-panel', restaurantId }: TermsContainerProps) => {
  const restaurant = useRestaurant();
  const giveawayAmount = restaurant?.giveawayAmount || '$500';
  const messages = useTermsMessages();
  const { data, loading } = useQuery(PopmenuTermsDocument, {
    fetchPolicy: 'cache-and-network',
  });

  const restaurantDocumentType = RESTAURANT_DOCUMENT_TYPES[termsType as keyof typeof RESTAURANT_DOCUMENT_TYPES];
  const documentsRestaurantId = restaurantId ?? restaurant?.id;

  const { data: restaurantData, loading: loadingRestaurantDocument } = useRestaurantPublishedLegalDocumentsQuery({
    fetchPolicy: 'cache-first',
    skip: !documentsRestaurantId || !restaurantDocumentType,
    variables: { restaurantId: documentsRestaurantId ?? 0 },
  });

  const restaurantDocument = restaurantData?.restaurant?.publishedLegalDocuments
    ?.find(document => document.documentType === restaurantDocumentType);

  if (loading || loadingRestaurantDocument || !data || !data.popmenuTerms) {
    return <Loading />;
  }

  // What the restaurant published for itself replaces Popmenu's document wherever it
  // is shown -- this page, the terms modals, and the ordering flow's dialogs.
  if (restaurantDocument) {
    return (
      <div id={id}>
        {!hideTitle && (
          <AH typography align="center" gutterBottom variant="h4">
            {messages[termsType]}
          </AH>
        )}
        <TermsHtmlContent htmlContent={restaurantDocument.content} />
      </div>
    );
  }

  // Dialogs use 'popmenuPrivacy' so restaurant docs don't override it;
  // the GraphQL document is still the 'privacy' field.
  const popmenuTermsField = termsType === 'popmenuPrivacy' ? 'privacy' : toCamelCase(termsType);
  const rawTerms: string = data.popmenuTerms[popmenuTermsField as keyof Omit<PopmenuTermsType, 'id'>];
  const termsWithReplacements = rawTerms.replace(/{{ review_reward_amount }}/g, giveawayAmount);

  const isHtmlContent = popmenuTermsField === 'privacy';

  let terms: string | React.ReactNode[];
  if (isHtmlContent) {
    terms = termsWithReplacements;
  } else {
    terms = reactStringReplace(termsWithReplacements, '{{ ccpa_request_link }}', () => (
      <a href="https://my.popmenu.com/do-not-sell-my-personal-info" rel="noopener" target="_blank">
        <FormattedMessage defaultMessage="here" />
      </a>
    ));
  }

  return (
    <div id={id}>
      {!hideTitle && (
        <AH typography align="center" gutterBottom variant="h4">
          {messages[termsType]}
        </AH>
      )}
      {isHtmlContent ? (
        <TermsHtmlContent htmlContent={termsWithReplacements} />
      ) : (
        <Typography style={{ whiteSpace: 'pre-wrap' }}>
          {terms}
        </Typography>
      )}
    </div>
  );
};

export default TermsContainer;
